// Group Template 4 — "Aurora" — a premium DARK, centered SaaS-style group landing
// page: deep charcoal canvas, neon pink→violet glows, a centered hero (eyebrow,
// big gradient headline, centered subtitle + chips + primary CTA), a large
// full-width media/carousel showcase, a clean 3-up stats row, then single-column
// dark sections with subtle white borders, a join CTA band and footer.
// Deliberately distinct from Template 2 "Nebula" (split hero / violet-cyan /
// Space Grotesk): this is centered, pink→violet, Plus Jakarta Sans. Same data
// contract + join / pricing / referral functionality.
import { Fragment, useEffect, useState } from "react";
import {
    Icon, Stars, splitTitle, rgba, shade, useGoogleFonts, orderedSections,
    Reveal, GuaranteeBadge, skillIconD, PolicyLinks, SOCIAL_ICONS, videoEmbedUrl,
} from "../course-templates/shared";
import GroupSwitcher from "../GroupSwitcher";
import InstructorAvatar from "../InstructorAvatar";
import PoweredByFlexiFunnels from "../PoweredByFlexiFunnels";
import { ReferralLink } from "../CommunityViews";

const DEFAULT_SKILLS = [
    "Exclusive trainings and insider knowledge",
    "High-value resources and downloadable guides",
    "Proven frameworks and strategies for real results",
    "Step-by-step learning content you can apply immediately",
    "Group discussions and support",
    "Access to special workshops, masterclasses, and events",
];

const escapeHtml = (s) => String(s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// Template 4's own SIGNATURE default palette — vivid pink → violet. Do NOT fall
// back to the community's primary colour, so an unsaved landing previews in the
// template's own signature colours.
const SIGNATURE = "#EC4899";
const SIGNATURE2 = "#7C3AED";

// Default money-back copy — the EXACT course landing default so the editor and
// live page render identical text.
const guaranteeDefaultHtml = (days = 30) =>
    `<p>We believe in the value of this group and are confident it will help you achieve your goals. That's why we offer a full ${days}-day risk-free money-back guarantee.</p><p>Explore everything inside, connect with the community, and put what you learn into action. If at any point within the first ${days} days you feel the group isn't right for you or doesn't meet your expectations, simply reach out to us.</p><p>We'll process your refund promptly—no questions asked.</p><p>There's absolutely no risk in getting started today, only the opportunity to learn, grow, and succeed with complete peace of mind.</p>`;

const FONT = "'Plus Jakarta Sans',sans-serif";
const BG = "#0c0f16";

export default function GroupTemplate4(ctx) {
    const {
        community, group, landing, primary,
        media = [], defaultImage,
        accessLabel, isPrivate, pricing, isPaid, amountLabel,
        joinLabel, skillHeading, skillList = [], shortDescription, postCount, courseStat,
        member, embedded = false, referral, token, gid, myGroups = [],
        onJoin, onLogin, onInstructor, onSwitchGroup, onDiscoverGroups,
    } = ctx;

    useGoogleFonts("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap");

    // Palette — the template's OWN signature (pink→violet), NOT the community's.
    const A = (landing.theme?.color1 && String(landing.theme.color1).trim()) || SIGNATURE;
    const B = (landing.theme?.color2 && String(landing.theme.color2).trim()) || SIGNATURE2;

    // Buttons use PRIMARY only. `grad` (A→B) is for DECORATIVE use only —
    // headline text, underlines, glows, feature blocks — never on a button.
    const btnGrad = `linear-gradient(135deg, ${A}, ${shade(A, -0.2)})`;
    const grad = `linear-gradient(135deg, ${A}, ${B})`;

    const t = splitTitle(landing.header?.headline || group.name);
    const sub = landing.header?.subheadline || shortDescription || "";
    const instructors = Array.isArray(group.instructors) && group.instructors.length
        ? group.instructors
        : (group.instructor ? [group.instructor] : []);
    const instructor = instructors[0] || null;
    const memberCount = group.member_count ?? 0;
    const mediaShow = landing.media_show !== false && media.length > 0;

    const skHeading = (skillHeading || "").trim() || "Skills you will walk away with";
    const skItems = (skillList && skillList.length ? skillList : DEFAULT_SKILLS).filter(Boolean);

    const aboutHtml = (landing.description || "").trim()
        ? landing.description
        : (group.description || "").trim()
            ? `<p>${escapeHtml(group.description)}</p>`
            : "";

    const order = orderedSections(landing.section_order).filter((id) => id !== "include");

    // Header nav — anchor links, shown only for sections that actually render.
    const navLinks = [
        landing.about?.show !== false && aboutHtml && { href: "#bn-about", label: "About" },
        landing.skill?.show !== false && skItems.length > 0 && { href: "#bn-skill", label: "Skills" },
        landing.instructor?.show !== false && instructor?.name && { href: "#bn-instructor", label: "Instructor" },
        landing.testimonials?.show && landing.testimonials.list?.length > 0 && { href: "#bn-reviews", label: "Reviews" },
        landing.faq?.show && landing.faq.list?.length > 0 && { href: "#bn-faq", label: "FAQ" },
    ].filter(Boolean);

    const social = landing.social || {};
    const socialLinks = Object.entries(SOCIAL_ICONS).filter(([k]) => social[k]);

    // Small white logo chip so the community logo reads on the dark canvas.
    const LogoChip = ({ imgClass }) => (
        community.logo && String(community.logo).trim()
            ? <span className="inline-flex items-center rounded-lg bg-white px-2.5 py-1.5"><img src={community.logo} alt={community.name} className={imgClass} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = `${process.env.PUBLIC_URL || ""}/images/flexicommunity-logo.png`; }} /></span>
            : <span className="inline-flex items-center rounded-lg bg-white px-2.5 py-1.5"><img src={`${process.env.PUBLIC_URL || ""}/images/flexicommunity-logo.png`} alt="FlexiCommunity" className={imgClass} /></span>
    );

    // Meta chips — dark frosted pills with neon-tinted icons.
    const chips = (
        <div className="flex flex-wrap items-center justify-center gap-2.5">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[.06] px-3.5 py-1.5 text-[13px] font-bold text-gray-200 backdrop-blur">
                <span className="inline-flex" style={{ color: A }}><Icon d={isPrivate ? "M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 0h10.5a1.5 1.5 0 011.5 1.5v6a1.5 1.5 0 01-1.5 1.5H6.75a1.5 1.5 0 01-1.5-1.5v-6a1.5 1.5 0 011.5-1.5z" : "M12 21a9 9 0 100-18 9 9 0 000 18zm0 0a8.95 8.95 0 003.6-.75M12 3a8.95 8.95 0 00-3.6.75M3.6 9h16.8M3.6 15h16.8"} className="h-4 w-4" stroke={2.2} /></span>
                {accessLabel}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[.06] px-3.5 py-1.5 text-[13px] font-bold text-gray-200 backdrop-blur">
                <span className="inline-flex" style={{ color: A }}><Icon d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" className="h-4 w-4" stroke={2} /></span>
                {memberCount} {memberCount === 1 ? "member" : "members"}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[.06] px-3.5 py-1.5 text-[13px] font-bold text-gray-200 backdrop-blur">
                <span className="inline-flex" style={{ color: A }}><Icon d={pricing === "free" ? "M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" : pricing === "free_trial" ? "M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" : "M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"} className="h-4 w-4" stroke={2} /></span>
                {pricing === "free" ? "Free" : pricing === "free_trial" ? `Free trial${amountLabel ? ` · ${amountLabel}` : ""}` : `Paid${amountLabel ? ` · ${amountLabel}` : ""}`}
            </span>
            {instructors.map((ins) => (
                <button key={ins.instructor_id} type="button" onClick={() => onInstructor && onInstructor(ins.instructor_id, A, true)} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[.06] py-1 pl-1 pr-3 text-[13px] font-bold text-gray-200 backdrop-blur transition hover:bg-white/[.12]">
                    <InstructorAvatar name={ins.name} image={ins.profile_image} primary={A} sizeClass="h-6 w-6" textClass="text-[10px]" /> <span className="underline decoration-dotted decoration-1 underline-offset-[3px]">{ins.name}</span><Icon d="M8.25 4.5l7.5 7.5-7.5 7.5" className="ml-1 h-3 w-3 opacity-70" stroke={2.5} />
                </button>
            ))}
        </div>
    );

    // The one and only button style — PRIMARY gradient (btnGrad), used everywhere.
    const JoinButton = ({ full }) => (
        <button onClick={() => onJoin(group)} style={{ background: btnGrad, boxShadow: `0 18px 40px -12px ${rgba(A, 0.6)}` }}
            className={`bn-shine inline-flex items-center justify-center gap-2 rounded-2xl px-7 py-4 text-[15px] font-extrabold text-white transition-all duration-200 hover:-translate-y-0.5 active:scale-[.98] ${full ? "w-full" : ""}`}>
            {!group.joined && <Icon d="M12 4.5v15m7.5-7.5h-15" className="h-5 w-5" stroke={2.4} />}
            <span>{joinLabel}</span>
        </button>
    );

    const stats = [["Posts", postCount], ["Members", memberCount], ["Courses", courseStat]];

    // ---- reorderable content blocks ----
    const blocks = {
        about: landing.about?.show !== false && aboutHtml && (
            <Reveal>
                <Card B={B}>
                    <SecHead id="bn-about" eyebrow="Overview" title="About this group" A={A} B={B} />
                    <div
                        className="mt-5 max-w-none text-[15px] leading-relaxed text-gray-300 [&_h1]:mb-2 [&_h1]:mt-6 [&_h1]:text-2xl [&_h1]:font-[800] [&_h1]:text-white [&_h2]:mb-2 [&_h2]:mt-5 [&_h2]:text-xl [&_h2]:font-[800] [&_h2]:text-white [&_h3]:mb-1.5 [&_h3]:mt-4 [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-white [&_h4]:mb-1 [&_h4]:mt-3 [&_h4]:font-bold [&_h4]:text-white [&_p]:mb-3 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_strong]:font-bold [&_strong]:text-gray-100 [&_b]:font-bold [&_b]:text-gray-100 [&_em]:italic [&_a]:font-semibold [&_a]:text-[color:var(--a)] [&_a]:underline first:[&_h1]:mt-0 first:[&_h2]:mt-0"
                        style={{ "--a": A }}
                        dangerouslySetInnerHTML={{ __html: aboutHtml }}
                    />
                </Card>
            </Reveal>
        ),
        skill: landing.skill?.show !== false && skItems.length > 0 && (
            <Reveal>
                <Card B={B}>
                    <SecHead id="bn-skill" eyebrow="What you'll learn" title={skHeading} A={A} B={B} />
                    <div className="mt-6 grid gap-3.5 sm:grid-cols-2">
                        {skItems.map((s, i) => (
                            <div key={i} className="flex items-start gap-3 rounded-2xl border border-white/[.08] bg-white/[.03] p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-white/20">
                                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-xl text-white" style={{ background: btnGrad, boxShadow: `0 6px 16px ${rgba(A, 0.5)}` }}><Icon d={skillIconD(landing.skill?.icon)} className="h-4 w-4" stroke={2.2} /></span>
                                <span className="text-[15px] font-medium leading-snug text-gray-300">{s}</span>
                            </div>
                        ))}
                    </div>
                    {landing.skill?.long_desc && <div className="mt-6 max-w-2xl text-[15px] leading-relaxed text-gray-400" dangerouslySetInnerHTML={{ __html: landing.skill.long_desc }} />}
                </Card>
            </Reveal>
        ),
        instructor: landing.instructor?.show !== false && instructor?.name && (
            <Reveal>
                <div id="bn-instructor" className="scroll-mt-24">
                    <div className="relative overflow-hidden rounded-[2rem] p-8 text-white shadow-[0_40px_90px_-40px_rgba(0,0,0,.8)] sm:p-12" style={{ background: grad }}>
                        <div className="pointer-events-none absolute -right-10 -top-10 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
                        <div className="pointer-events-none absolute -bottom-16 left-1/4 h-52 w-52 rounded-full bg-black/20 blur-3xl" />
                        <div className="relative">
                            <span className="text-[0.72rem] font-extrabold uppercase tracking-[0.18em] text-white/80">Instructor</span>
                            <h2 className="mt-2 text-[1.7rem] font-[800] leading-tight tracking-tight sm:text-[2rem]">{instructors.length > 1 ? "Meet the instructors" : "Meet the instructor"}</h2>
                        </div>
                        <div className="relative space-y-10">
                        {instructors.map((ins) => (
                        <div key={ins.instructor_id} className="mt-8 grid items-center gap-8 sm:grid-cols-[248px_1fr] sm:gap-12">
                            <div className="relative order-1 mx-auto">
                                <span className="bn-float absolute -left-3 -top-3 z-10 flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-[0_10px_24px_rgba(0,0,0,.4)]" style={{ color: A }}>
                                    <Icon d="M12 2l2.4 7.6L22 12l-7.6 2.4L12 22l-2.4-7.6L2 12l7.6-2.4z" className="h-6 w-6 fill-current" stroke={0} />
                                </span>
                                <div className="h-56 w-56 overflow-hidden rounded-[1.6rem] bg-white/10 shadow-[0_24px_50px_-20px_rgba(0,0,0,.6)] ring-4 ring-white/40 sm:w-[248px]">
                                    {ins.profile_image
                                        ? <img src={ins.profile_image} alt="" className="h-full w-full object-cover" />
                                        : <span className="flex h-full w-full items-center justify-center bg-white/15 text-6xl font-[800] text-white">{ins.name.charAt(0).toUpperCase()}</span>}
                                </div>
                            </div>
                            <div className="order-2 text-center sm:text-left">
                                <h3 className="text-[1.9rem] font-[800] leading-tight sm:text-[2.4rem]">{ins.name}</h3>
                                <span className="mx-auto mt-3 block h-1 w-14 rounded-full bg-white/70 sm:mx-0" />
                                {ins.short_description
                                    ? <p className="mt-5 text-[15.5px] leading-relaxed text-white/90">{ins.short_description}</p>
                                    : <p className="mt-5 text-[15px] italic text-white/70">Meet the person leading this community.</p>}
                            </div>
                        </div>
                        ))}
                        </div>
                    </div>
                </div>
            </Reveal>
        ),
        testimonial: landing.testimonials?.show && landing.testimonials.list?.length > 0 && (
            <Reveal>
                <div id="bn-reviews" className="scroll-mt-24">
                    <SecHead eyebrow="Testimonials" title={landing.testimonials.heading || "What students are saying"} A={A} B={B} center />
                    <div className="mt-8 grid gap-5 sm:grid-cols-2">
                        {landing.testimonials.list.map((r, i) => (
                            <div key={i} className="group relative overflow-hidden rounded-[1.75rem] border border-white/[.08] bg-white/[.03] p-6 shadow-[0_30px_70px_-40px_rgba(0,0,0,.8)] transition-all duration-300 hover:-translate-y-1.5 sm:p-7"
                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = rgba(B, 0.5); e.currentTarget.style.boxShadow = `0 34px 70px -34px ${rgba(B, 0.5)}`; }}
                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = ""; e.currentTarget.style.boxShadow = ""; }}>
                                <span className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-2xl text-white transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-110" style={{ background: btnGrad, boxShadow: `0 10px 22px ${rgba(A, 0.5)}` }}>
                                    <Icon d="M8.25 6.75A2.25 2.25 0 006 9v3a2.25 2.25 0 002.25 2.25H9v.75a2.25 2.25 0 01-2.25 2.25.75.75 0 000 1.5A3.75 3.75 0 0010.5 15V9A2.25 2.25 0 008.25 6.75zm7.5 0A2.25 2.25 0 0013.5 9v3a2.25 2.25 0 002.25 2.25h.75v.75a2.25 2.25 0 01-2.25 2.25.75.75 0 000 1.5A3.75 3.75 0 0018 15V9a2.25 2.25 0 00-2.25-2.25z" className="h-5 w-5 fill-current" stroke={0} />
                                </span>
                                <div className="relative">
                                    <Stars value={r.rating || 5} className="h-4 w-4" color={A} empty="rgba(255,255,255,.15)" />
                                    <p className="mt-4 text-[15.5px] leading-relaxed text-gray-200">{r.quote}</p>
                                    <div className="mt-6 flex items-center gap-3 border-t border-white/10 pt-5">
                                        <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl text-sm font-bold text-white ring-2 ring-white/10" style={{ background: btnGrad }}>{r.avatar ? <img src={r.avatar} alt="" className="h-full w-full object-cover" /> : (r.name || "M").charAt(0).toUpperCase()}</span>
                                        <div className="min-w-0 flex-1"><p className="truncate text-sm font-extrabold text-white">{r.name}</p>{r.role && <p className="truncate text-[13px] text-gray-400">{r.role}</p>}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Reveal>
        ),
        // Money-back only for paid / trial groups (nothing to refund on free ones).
        moneyback: landing.guarantee?.show && pricing !== "free" && (
            <Reveal>
                <div className="relative overflow-hidden rounded-[2rem] border border-white/[.08] bg-white/[.03] p-8 shadow-[0_30px_80px_-40px_rgba(0,0,0,.8)] sm:p-10">
                    <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full opacity-40 blur-3xl" style={{ background: rgba(A, 0.3) }} />
                    <div className="pointer-events-none absolute -bottom-16 -left-10 h-56 w-56 rounded-full opacity-30 blur-3xl" style={{ background: rgba(B, 0.3) }} />
                    <div className="relative grid items-center gap-7 sm:grid-cols-[auto_1fr]">
                        <div className="mx-auto rounded-full p-3" style={{ background: grad, boxShadow: `0 18px 40px -12px ${rgba(A, 0.6)}` }}>
                            <div className="rounded-full bg-white p-2"><GuaranteeBadge variant={landing.guarantee.badge || 1} days={landing.guarantee.days || 30} accent={A} className="h-28 w-28" /></div>
                        </div>
                        <div className="text-center sm:text-left">
                            <h3 className="text-2xl font-[800] sm:text-[1.9rem]" style={{ background: grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>{landing.guarantee.heading?.trim() || `${landing.guarantee.days || 30}-Day Money-Back Guarantee`}</h3>
                            <div className="mt-3 text-[15px] leading-relaxed text-gray-300 [&_p]:mt-2" dangerouslySetInnerHTML={{ __html: landing.guarantee.description?.trim() || guaranteeDefaultHtml(landing.guarantee.days || 30) }} />
                        </div>
                    </div>
                </div>
            </Reveal>
        ),
        faq: landing.faq?.show && landing.faq.list?.length > 0 && (
            <Reveal>
                <div id="bn-faq" className="scroll-mt-24">
                    <SecHead eyebrow="FAQ" title={landing.faq.heading || "Frequently asked questions"} A={A} B={B} center />
                    <div className="mx-auto mt-6 max-w-2xl space-y-3">
                        {landing.faq.list.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} btnGrad={btnGrad} />)}
                    </div>
                </div>
            </Reveal>
        ),
    };

    return (
        <div className={embedded ? "bg-[#0c0f16]" : "min-h-screen bg-[#0c0f16] text-gray-200"} style={{ fontFamily: FONT }}>
            <style>{`
                html{scroll-behavior:smooth}
                @keyframes bnFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-16px)}}
                @keyframes bnShine{to{transform:translateX(240%) skewX(-18deg)}}
                @keyframes bnFadeIn{from{opacity:0}to{opacity:1}}
                .bn-float{animation:bnFloat 9s ease-in-out infinite}
                .bn-shine{position:relative;overflow:hidden;isolation:isolate}
                .bn-shine::after{content:"";position:absolute;inset:0 auto 0 0;width:45%;transform:translateX(-180%) skewX(-18deg);background:linear-gradient(90deg,transparent,rgba(255,255,255,.35),transparent);z-index:1;pointer-events:none}
                .bn-shine:hover::after{animation:bnShine .95s ease}
            `}</style>

            {!embedded && (
                <div className="pointer-events-none fixed inset-0 overflow-hidden">
                    <div className="bn-float absolute -left-40 -top-40 h-[34rem] w-[34rem] rounded-full opacity-30 blur-[130px]" style={{ background: rgba(A, 0.5) }} />
                    <div className="bn-float absolute -right-40 top-40 h-[32rem] w-[32rem] rounded-full opacity-25 blur-[130px]" style={{ background: rgba(B, 0.5), animationDelay: "-4s" }} />
                    <div className="bn-float absolute bottom-0 left-1/3 h-[28rem] w-[28rem] rounded-full opacity-20 blur-[130px]" style={{ background: rgba(B, 0.4), animationDelay: "-2s" }} />
                </div>
            )}

            {/* header */}
            {!embedded && (
                <header className="sticky top-0 z-30 border-b border-white/[.06] bg-[#0c0f16]/80 backdrop-blur-xl">
                    <div className="relative mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
                        <div className="flex items-center gap-2.5">
                            <LogoChip imgClass="h-7 w-auto max-w-[150px] object-contain" />
                            {member && myGroups.length > 0 && <GroupSwitcher groups={myGroups} currentToken={gid} primary={A} onSelect={onSwitchGroup} onDiscover={onDiscoverGroups} />}
                        </div>
                        {navLinks.length > 0 && (
                            <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-9 lg:flex">
                                {navLinks.map((l) => (
                                    <a key={l.href} href={l.href} className="group relative py-1 text-[14px] font-bold text-gray-400 transition-colors duration-200 hover:text-white">
                                        {l.label}
                                        <span className="absolute -bottom-0.5 left-0 h-[2px] w-0 rounded-full transition-all duration-300 ease-out group-hover:w-full" style={{ background: grad }} />
                                    </a>
                                ))}
                            </nav>
                        )}
                        {member ? (
                            <button onClick={() => onJoin(group)} style={{ background: btnGrad }} className="rounded-xl px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5">{joinLabel}</button>
                        ) : isPaid ? (
                            <button onClick={() => onJoin(group)} style={{ background: btnGrad }} className="rounded-xl px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5">Get Access</button>
                        ) : (
                            <button onClick={() => onLogin(group)} style={{ background: btnGrad }} className="rounded-xl px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5">Log In</button>
                        )}
                    </div>
                </header>
            )}

            <main className="relative mx-auto max-w-6xl px-5 pb-20 pt-12 sm:pt-16">
                {/* CENTERED hero */}
                <section className="text-center">
                    <Reveal>
                        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[.05] px-4 py-1.5 text-[12px] font-extrabold uppercase tracking-[0.18em] text-gray-300 backdrop-blur">
                            <span className="h-1.5 w-1.5 rounded-full" style={{ background: B, boxShadow: `0 0 10px ${B}` }} />
                            {community.name}
                        </div>
                        <h1 className="mx-auto mt-7 max-w-4xl text-[2.5rem] font-[800] leading-[1.05] tracking-tight text-white sm:text-[3.6rem]">
                            {t.before}
                            {t.accent && <> <span style={{ backgroundImage: grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>{t.accent}</span></>}
                            {t.after ? ` ${t.after}` : ""}
                        </h1>
                        {sub && <p className="mx-auto mt-6 max-w-2xl text-[16px] leading-relaxed text-gray-400 sm:text-[17px]">{sub}</p>}
                        <div className="mt-8">{chips}</div>
                        <div className="mt-9 flex flex-col items-center gap-3">
                            {referral && group.joined
                                ? <div className="w-full max-w-md"><ReferralLink token={token} groupToken={gid} urlToken={referral.urlToken} primary={A} points={referral.points} /></div>
                                : <>
                                    <JoinButton />
                                    <span className="text-sm font-semibold text-gray-500">Join {memberCount > 0 ? `${memberCount}+ members` : "the community"}</span>
                                </>}
                        </div>
                    </Reveal>
                </section>

                {/* full-width media / carousel showcase */}
                <Reveal>
                    <div className="mx-auto mt-14 max-w-5xl sm:mt-16">
                        {mediaShow
                            ? <Carousel media={media} defaultImage={defaultImage} A={A} B={B} title={group.name} />
                            : (
                                <div className="relative">
                                    <div className="absolute -inset-3 -z-10 rounded-[2.5rem] opacity-40 blur-2xl" style={{ background: grad }} />
                                    <div className="relative aspect-[16/10] w-full overflow-hidden rounded-[2rem] shadow-[0_40px_90px_-40px_rgba(0,0,0,.9)] ring-1 ring-white/10 sm:aspect-[16/9]">
                                        <img src={group.banner || defaultImage} alt={group.name} className="h-full w-full object-cover" onError={(e) => { if (e.currentTarget.src !== defaultImage) e.currentTarget.src = defaultImage; }} />
                                    </div>
                                </div>
                            )}
                    </div>
                </Reveal>

                {/* stats row */}
                <Reveal>
                    <div className="mx-auto mt-12 grid max-w-3xl grid-cols-3 gap-4 sm:mt-14 sm:gap-6">
                        {stats.map(([lbl, val], i) => (
                            <div key={lbl} className="rounded-[1.75rem] border border-white/[.08] bg-white/[.03] p-5 text-center shadow-[0_30px_70px_-44px_rgba(0,0,0,.8)] sm:p-7">
                                <div className="text-3xl font-[800] leading-none sm:text-4xl" style={{ color: i === 1 ? B : A }}>{Number(val) || 0}</div>
                                <div className="mt-2 text-[11px] font-extrabold uppercase tracking-wider text-gray-500">{lbl}</div>
                            </div>
                        ))}
                    </div>
                </Reveal>

                {/* content sections */}
                <div className="mx-auto mt-20 max-w-4xl space-y-8 sm:space-y-10">
                    {order.map((id) => <Fragment key={id}>{blocks[id]}</Fragment>)}
                </div>

                {/* join CTA band */}
                <Reveal>
                    <div className="relative mx-auto mt-16 max-w-4xl overflow-hidden rounded-[2.5rem] border border-white/[.08] bg-white/[.03] p-10 text-center shadow-[0_40px_90px_-44px_rgba(0,0,0,.9)] sm:p-16">
                        <div className="pointer-events-none absolute -right-16 -top-20 h-72 w-72 rounded-full opacity-40 blur-3xl" style={{ background: rgba(A, 0.3) }} />
                        <div className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-full opacity-30 blur-3xl" style={{ background: rgba(B, 0.3) }} />
                        <div className="relative mx-auto max-w-xl">
                            <h3 className="text-[1.8rem] font-[800] text-white sm:text-[2.3rem]">{group.name}</h3>
                            {shortDescription && <p className="mx-auto mt-4 max-w-md text-[15px] leading-relaxed text-gray-400">{shortDescription}</p>}
                            <div className="mx-auto mt-8 flex max-w-xs justify-center">
                                {referral && group.joined
                                    ? <div className="w-full"><ReferralLink token={token} groupToken={gid} urlToken={referral.urlToken} primary={A} points={referral.points} /></div>
                                    : <JoinButton full />}
                            </div>
                            {community?.show_branding && <PoweredByFlexiFunnels className="mt-6 !text-gray-400 hover:!text-gray-200" />}
                        </div>
                    </div>
                </Reveal>
            </main>

            {/* footer */}
            {(
                <footer className="relative overflow-hidden border-t border-white/[.06] bg-[#0c0f16]">
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${rgba(A, 0.6)}, transparent)` }} />
                    <div className="pointer-events-none absolute -bottom-24 left-1/2 h-48 w-[36rem] -translate-x-1/2 rounded-full opacity-30 blur-3xl" style={{ background: rgba(B, 0.25) }} />
                    <div className="relative mx-auto max-w-6xl px-5 py-7">
                        <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
                            <div className="flex items-center gap-3">
                                <div>
                                    <p className="text-[15px] font-[800] text-white">{community.name}</p>
                                    <p className="text-[13px] text-gray-500">{group.name}</p>
                                </div>
                            </div>

                            <div className="flex flex-col items-center gap-4 sm:flex-row sm:gap-6">
                                {socialLinks.length > 0 && (
                                    <div className="flex items-center gap-2.5">
                                        {socialLinks.map(([k, d]) => (
                                            <a key={k} href={social[k]} target="_blank" rel="noopener noreferrer" aria-label={k}
                                                className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/[.03] text-gray-400 transition-all duration-200 hover:-translate-y-1 hover:border-transparent hover:text-white"
                                                onMouseEnter={(e) => { e.currentTarget.style.background = grad; e.currentTarget.style.boxShadow = `0 10px 22px ${rgba(A, 0.5)}`; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.background = ""; e.currentTarget.style.boxShadow = ""; }}>
                                                <svg viewBox="0 0 24 24" fill="currentColor" className="h-[18px] w-[18px]"><path d={d} /></svg>
                                            </a>
                                        ))}
                                    </div>
                                )}
                                <PolicyLinks policies={landing.policies} accent={A} className="text-gray-400" />
                            </div>
                        </div>
                    </div>
                </footer>
            )}
        </div>
    );
}

// A dark premium rounded card wrapper for content sections.
function Card({ B, children }) {
    return (
        <div className="relative overflow-hidden rounded-[2rem] border border-white/[.08] bg-white/[.03] p-7 shadow-[0_30px_80px_-40px_rgba(0,0,0,.8)] sm:p-10">
            <div className="pointer-events-none absolute -right-20 -top-20 h-52 w-52 rounded-full opacity-30 blur-3xl" style={{ background: rgba(B, 0.22) }} />
            <div className="relative">{children}</div>
        </div>
    );
}

// Section header — neon eyebrow dot + gradient underline. `center` centers it.
function SecHead({ id, eyebrow, title, sub, A, B, center }) {
    return (
        <div id={id} className={`${center ? "text-center" : ""} scroll-mt-24`}>
            <span className={`inline-flex items-center gap-2 text-[0.72rem] font-extrabold uppercase tracking-[0.18em] text-gray-400 ${center ? "justify-center" : ""}`}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: B, boxShadow: `0 0 10px ${B}` }} />
                {eyebrow}
            </span>
            <h2 className="mt-3 text-[1.7rem] font-[800] leading-tight tracking-tight text-white sm:text-[2.1rem]">{title}</h2>
            <span className={`mt-4 block h-1 w-14 rounded-full ${center ? "mx-auto" : ""}`} style={{ background: `linear-gradient(90deg, ${A}, ${B})` }} />
            {sub && <div className={`mt-4 text-[15px] leading-relaxed text-gray-400 ${center ? "mx-auto max-w-2xl" : "max-w-2xl"}`} dangerouslySetInnerHTML={{ __html: sub }} />}
        </div>
    );
}

// Media carousel — auto-advance, pause on hover / video, arrows + dot indicators.
function Carousel({ media, defaultImage, A, B, title }) {
    const [idx, setIdx] = useState(0);
    const [hover, setHover] = useState(false);
    const n = media.length;
    const cur = media[idx] || {};
    const isVideo = cur.type === "video";
    useEffect(() => {
        // Pause auto-advance while hovering, or while a video slide shows.
        if (n <= 1 || hover || isVideo) return undefined;
        const timer = setInterval(() => setIdx((i) => (i + 1) % n), 4800);
        return () => clearInterval(timer);
    }, [n, hover, isVideo]);
    const go = (d) => setIdx((i) => (i + d + n) % n);
    return (
        <div className="relative">
            <div className="absolute -inset-3 -z-10 rounded-[2.5rem] opacity-40 blur-2xl" style={{ background: `linear-gradient(135deg, ${A}, ${B})` }} />
            <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} className="group relative aspect-[16/10] w-full overflow-hidden rounded-[2rem] bg-[#12151d] shadow-[0_40px_90px_-40px_rgba(0,0,0,.9)] ring-1 ring-white/10 sm:aspect-[16/9]">
                {cur.type === "video" ? (
                    <iframe key={idx} src={videoEmbedUrl(cur.platform, cur.url)} title={title} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                ) : (
                    <img key={idx} src={cur.image || defaultImage} alt={title} className="h-full w-full animate-[bnFadeIn_.6s_ease] object-cover" onError={(e) => { if (e.currentTarget.src !== defaultImage) e.currentTarget.src = defaultImage; }} />
                )}
                {n > 1 && (
                    <>
                        <button type="button" onClick={() => go(-1)} aria-label="Previous" className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-gray-900 shadow-lg backdrop-blur transition hover:bg-white sm:opacity-0 sm:group-hover:opacity-100">
                            <Icon d="M15.75 19.5L8.25 12l7.5-7.5" className="h-5 w-5" stroke={2.4} />
                        </button>
                        <button type="button" onClick={() => go(1)} aria-label="Next" className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-gray-900 shadow-lg backdrop-blur transition hover:bg-white sm:opacity-0 sm:group-hover:opacity-100">
                            <Icon d="M8.25 4.5l7.5 7.5-7.5 7.5" className="h-5 w-5" stroke={2.4} />
                        </button>
                        <div className="absolute inset-x-0 bottom-4 flex items-center justify-center gap-2">
                            {media.map((_, i) => (
                                <button key={i} type="button" onClick={() => setIdx(i)} aria-label={`Go to slide ${i + 1}`}
                                    className="h-2 rounded-full transition-all" style={{ width: i === idx ? 26 : 8, background: i === idx ? "#fff" : "rgba(255,255,255,.5)" }} />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function FaqItem({ q, a, btnGrad }) {
    const [open, setOpen] = useState(false);
    return (
        <div className={`overflow-hidden rounded-2xl border bg-white/[.03] transition-colors ${open ? "border-white/15" : "border-white/[.08]"}`}>
            <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left">
                <span className="text-[15px] font-extrabold text-white">{q}</span>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white transition-transform duration-300" style={{ background: btnGrad, transform: open ? "rotate(45deg)" : "none" }}><Icon d="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" stroke={2.4} /></span>
            </button>
            <div className="grid transition-all duration-300" style={{ gridTemplateRows: open ? "1fr" : "0fr" }}>
                <div className="overflow-hidden"><p className="px-5 pb-5 text-[15px] leading-relaxed text-gray-400">{a}</p></div>
            </div>
        </div>
    );
}
